import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { DataTable, type Column } from './DataTable';
import { FormShell, Field, fieldErrorsFromApiError } from './FormShell';
import { useToast } from './Toast';
import { useOffices } from '../hooks/useOffices';
import { useServiceLines } from '../hooks/useServiceLines';
import { usePeopleSearch } from '../hooks/usePeopleSearch';
import { useProjectsSearch } from '../hooks/useProjectsSearch';
import { useCreateSavedFilter, useUpdateSavedFilter } from '../hooks/useSavedFilters';
import type {
  PersonSearchResultDto,
  ProjectSearchResultDto,
  QueryOptions,
  SavedFilterDto,
} from '../lib/types';

interface Chip {
  id: number;
  label: string;
}

interface SavedFilterPickerProps {
  open: boolean;
  onClose: () => void;
  /** Set to edit an existing saved filter; null to create a new one. */
  editingFilter: SavedFilterDto | null;
  /** Pre-population for "Save current selection as…" (create mode only). */
  initialPersonIds?: number[];
  initialPersonNames?: string[];
  initialProjectIds?: number[];
  initialProjectNames?: string[];
  onSaved: (filter: SavedFilterDto) => void;
}

/**
 * Two independent searches (People / Projects) feeding a single named saved filter. A people
 * search never surfaces projects and vice versa — each tab is its own DataTable instance.
 */
export function SavedFilterPicker({
  open,
  onClose,
  editingFilter,
  initialPersonIds,
  initialPersonNames,
  initialProjectIds,
  initialProjectNames,
  onSaved,
}: SavedFilterPickerProps) {
  const [tab, setTab] = useState<'people' | 'projects'>('people');
  const [name, setName] = useState('');
  const [people, setPeople] = useState<Chip[]>([]);
  const [projects, setProjects] = useState<Chip[]>([]);
  const [apiErr, setApiErr] = useState<unknown>(null);

  const createMutation = useCreateSavedFilter();
  const updateMutation = useUpdateSavedFilter();
  const { success, error: toastError } = useToast();

  useEffect(() => {
    if (!open) return;
    setTab('people');
    setApiErr(null);
    if (editingFilter) {
      setName(editingFilter.name);
      setPeople(editingFilter.personIds.map((id, i) => ({ id, label: editingFilter.personNames[i] ?? String(id) })));
      setProjects(editingFilter.projectIds.map((id, i) => ({ id, label: editingFilter.projectNames[i] ?? String(id) })));
    } else {
      setName('');
      setPeople((initialPersonIds ?? []).map((id, i) => ({ id, label: initialPersonNames?.[i] ?? String(id) })));
      setProjects((initialProjectIds ?? []).map((id, i) => ({ id, label: initialProjectNames?.[i] ?? String(id) })));
    }
    // Only re-run when the modal opens or the target filter changes — not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingFilter]);

  function addPerson(p: PersonSearchResultDto) {
    setPeople((prev) => (prev.some((c) => c.id === p.id) ? prev : [...prev, { id: p.id, label: p.displayName }]));
  }

  function addProject(p: ProjectSearchResultDto) {
    setProjects((prev) =>
      prev.some((c) => c.id === p.id) ? prev : [...prev, { id: p.id, label: `${p.jobNumber} - ${p.name}` }],
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSave = name.trim().length > 0 && (people.length > 0 || projects.length > 0);

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSave) return;
    setApiErr(null);
    const req = {
      name: name.trim(),
      personIds: people.map((p) => p.id),
      projectIds: projects.map((p) => p.id),
    };
    try {
      const saved = editingFilter
        ? await updateMutation.mutateAsync({ id: editingFilter.id, req })
        : await createMutation.mutateAsync(req);
      success(editingFilter ? 'Saved filter updated.' : 'Saved filter created.');
      onSaved(saved);
      onClose();
    } catch (err) {
      setApiErr(err);
      toastError('Could not save the filter.');
    }
  }

  const fieldErrors = fieldErrorsFromApiError(apiErr);
  const formError =
    apiErr && Object.keys(fieldErrors).length === 0 ? 'Could not save the filter. Try again.' : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingFilter ? 'Edit saved filter' : 'Save current selection as…'}
      size="lg"
    >
      <FormShell onSubmit={handleSave} formError={formError}>
        <Field
          label="Filter name"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          required
          errors={fieldErrors}
        />

        <div>
          <div role="tablist" aria-label="Search type" className="flex border-b border-slate-200 mb-3">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'people'}
              onClick={() => setTab('people')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === 'people' ? 'border-brand-blue text-brand-blue' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              People
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'projects'}
              onClick={() => setTab('projects')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === 'projects' ? 'border-brand-blue text-brand-blue' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Projects
            </button>
          </div>

          {tab === 'people' ? (
            <PeopleSearchTab selectedIds={new Set(people.map((p) => p.id))} onAdd={addPerson} />
          ) : (
            <ProjectsSearchTab selectedIds={new Set(projects.map((p) => p.id))} onAdd={addProject} />
          )}
        </div>

        <SelectionPanel
          label="People"
          chips={people}
          onRemove={(id) => setPeople((prev) => prev.filter((c) => c.id !== id))}
        />
        <SelectionPanel
          label="Projects"
          chips={projects}
          onRemove={(id) => setProjects((prev) => prev.filter((c) => c.id !== id))}
        />

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave || isPending}
            className="px-3 py-1.5 rounded bg-brand-blue text-white hover:bg-brand-blue/90 disabled:opacity-50"
          >
            {isPending ? 'Saving…' : editingFilter ? 'Save changes' : 'Save filter'}
          </button>
        </div>
      </FormShell>
    </Modal>
  );
}

function SelectionPanel({
  label,
  chips,
  onRemove,
}: {
  label: string;
  chips: Chip[];
  onRemove: (id: number) => void;
}) {
  if (chips.length === 0) return null;
  return (
    <div>
      <span className="text-xs font-medium text-slate-500">{label} ({chips.length})</span>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {chips.map((c) => (
          <span
            key={c.id}
            className="inline-flex items-center gap-1 bg-brand-blue/10 text-brand-blue text-xs rounded-full pl-2.5 pr-1 py-1"
          >
            {c.label}
            <button
              type="button"
              onClick={() => onRemove(c.id)}
              aria-label={`Remove ${c.label}`}
              className="hover:text-brand-blue/70 leading-none text-sm"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function OfficeServiceLineToolbar({
  office,
  onOfficeChange,
  serviceLine,
  onServiceLineChange,
}: {
  office: string;
  onOfficeChange: (v: string) => void;
  serviceLine: string;
  onServiceLineChange: (v: string) => void;
}) {
  const { data: offices } = useOffices();
  const { data: serviceLines } = useServiceLines();

  return (
    <div className="flex items-center gap-2">
      <select
        value={office}
        onChange={(e) => onOfficeChange(e.target.value)}
        aria-label="Filter by office"
        className="border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
      >
        <option value="">All offices</option>
        {(offices ?? []).map((o) => (
          <option key={o.id} value={o.name}>{o.name}</option>
        ))}
      </select>
      <select
        value={serviceLine}
        onChange={(e) => onServiceLineChange(e.target.value)}
        aria-label="Filter by service line"
        className="border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
      >
        <option value="">All service lines</option>
        {(serviceLines ?? []).map((s) => (
          <option key={s.id} value={s.name}>{s.name}</option>
        ))}
      </select>
    </div>
  );
}

function PeopleSearchTab({
  selectedIds,
  onAdd,
}: {
  selectedIds: Set<number>;
  onAdd: (p: PersonSearchResultDto) => void;
}) {
  const [options, setOptions] = useState<QueryOptions>({ page: 1, pageSize: 10 });
  const [office, setOffice] = useState('');
  const [serviceLine, setServiceLine] = useState('');
  const { data, isLoading, error } = usePeopleSearch({ ...options, office: office || undefined, serviceLine: serviceLine || undefined });

  const columns: Column<PersonSearchResultDto>[] = [
    { header: 'Name', render: (p) => p.displayName },
    { header: 'Email', render: (p) => p.email ?? '—' },
    { header: 'Region', render: (p) => p.regionCode ?? '—' },
    {
      header: '',
      render: (p) =>
        selectedIds.has(p.id) ? (
          <span className="text-xs text-slate-400">Added</span>
        ) : (
          <button type="button" onClick={() => onAdd(p)} className="text-brand-blue text-xs hover:underline">
            Add
          </button>
        ),
    },
  ];

  return (
    <DataTable
      data={data}
      isLoading={isLoading}
      error={error}
      options={options}
      onOptionsChange={setOptions}
      columns={columns}
      rowKey={(p) => p.id}
      emptyState="No people match your search."
      searchable
      searchPlaceholder="Search people by name…"
      toolbar={
        <OfficeServiceLineToolbar
          office={office}
          onOfficeChange={(v) => { setOffice(v); setOptions((o) => ({ ...o, page: 1 })); }}
          serviceLine={serviceLine}
          onServiceLineChange={(v) => { setServiceLine(v); setOptions((o) => ({ ...o, page: 1 })); }}
        />
      }
    />
  );
}

function ProjectsSearchTab({
  selectedIds,
  onAdd,
}: {
  selectedIds: Set<number>;
  onAdd: (p: ProjectSearchResultDto) => void;
}) {
  const [options, setOptions] = useState<QueryOptions>({ page: 1, pageSize: 10 });
  const [office, setOffice] = useState('');
  const [serviceLine, setServiceLine] = useState('');
  const { data, isLoading, error } = useProjectsSearch({ ...options, office: office || undefined, serviceLine: serviceLine || undefined });

  const columns: Column<ProjectSearchResultDto>[] = [
    { header: 'Job Number', render: (p) => <span className="font-mono text-xs">{p.jobNumber}</span> },
    { header: 'Project Name', render: (p) => <span className="max-w-xs truncate block">{p.name}</span> },
    { header: 'Account', render: (p) => p.account ?? '—' },
    { header: 'Service Line', render: (p) => p.serviceLine ?? '—' },
    {
      header: '',
      render: (p) =>
        selectedIds.has(p.id) ? (
          <span className="text-xs text-slate-400">Added</span>
        ) : (
          <button type="button" onClick={() => onAdd(p)} className="text-brand-blue text-xs hover:underline">
            Add
          </button>
        ),
    },
  ];

  return (
    <DataTable
      data={data}
      isLoading={isLoading}
      error={error}
      options={options}
      onOptionsChange={setOptions}
      columns={columns}
      rowKey={(p) => p.id}
      emptyState="No projects match your search."
      searchable
      searchPlaceholder="Search by job number or project name…"
      toolbar={
        <OfficeServiceLineToolbar
          office={office}
          onOfficeChange={(v) => { setOffice(v); setOptions((o) => ({ ...o, page: 1 })); }}
          serviceLine={serviceLine}
          onServiceLineChange={(v) => { setServiceLine(v); setOptions((o) => ({ ...o, page: 1 })); }}
        />
      }
    />
  );
}
